import React, { useState } from 'react';
import { Users, HelpCircle, Link2, Search, ChevronDown, Upload, Info, Clock, DollarSign, TrendingUp, Share2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopHeaderBar from '../components/TopHeaderBar';

const MERCOR_PURPLE = '#5B3DC5';

/* ═══ Onboarding Modals ═══ */
const EarningsSharingModal = ({ isOpen, onClose, onContinue }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ padding: '16px' }}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white shadow-2xl w-full" style={{ borderRadius: '14px', maxWidth: '520px' }}>
        <div style={{ padding: '32px 36px 28px' }}>
          <div className="flex justify-center" style={{ marginBottom: '20px' }}>
            <div className="flex items-center justify-center" style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#EDE9FE' }}>
              <DollarSign style={{ width: '24px', height: '24px', color: MERCOR_PURPLE }} />
            </div>
          </div>
          <h2 className="text-center" style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px' }}>New Earnings-Sharing Model</h2>
          <p className="text-center" style={{ fontSize: '13px', color: '#6B7280', marginBottom: '24px', lineHeight: '1.6' }}>
            You now earn <span style={{ fontWeight: 700, color: MERCOR_PURPLE }}>20% of everything</span> your referral earns until you reach your Earnings Cap.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
            {[
              { icon: Clock, title: 'Earn from their first billable hour', sub: '' },
              { icon: DollarSign, title: '20% of all their earnings', sub: 'Hourly, bonuses, and one-time payments' },
              { icon: TrendingUp, title: 'Earnings across all projects', sub: 'Until you reach your Earnings Cap' },
            ].map((item, i) => (
              <div key={i} className="flex items-start" style={{ gap: '14px' }}>
                <div className="flex items-center justify-center shrink-0" style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#F5F3FF' }}>
                  <item.icon style={{ width: '18px', height: '18px', color: MERCOR_PURPLE }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>{item.title}</p>
                  {item.sub && <p style={{ fontSize: '12px', color: '#6B7280' }}>{item.sub}</p>}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center" style={{ gap: '12px' }}>
            <button onClick={onClose} className="flex-1 font-medium hover:bg-gray-50"
              style={{ padding: '12px 0', fontSize: '14px', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '10px' }}>Maybe later</button>
            <button onClick={onContinue} className="flex-1 font-semibold text-white hover:opacity-90"
              style={{ padding: '12px 0', fontSize: '14px', backgroundColor: MERCOR_PURPLE, borderRadius: '10px' }}>Continue</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EarningsCapModal = ({ isOpen, onClose, onNext, onBack }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ padding: '16px' }}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white shadow-2xl w-full" style={{ borderRadius: '14px', maxWidth: '520px' }}>
        <div style={{ padding: '32px 36px 28px' }}>
          <div className="flex justify-center" style={{ marginBottom: '20px' }}>
            <div className="flex items-center justify-center" style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#EDE9FE' }}>
              <svg style={{ width: '24px', height: '24px', color: MERCOR_PURPLE }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" /></svg>
            </div>
          </div>
          <h2 className="text-center" style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px' }}>How your Earnings Cap works</h2>
          <p className="text-center" style={{ fontSize: '13px', color: '#6B7280', marginBottom: '24px', lineHeight: '1.6' }}>
            Your cap is set by your referral's first accepted job offer.<br />
            Example: Your referral accepts their first job at <strong>$50/hour</strong>
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
            {[
              { icon: Clock, title: 'Earnings Cap = 4× hourly rate', sub: '$50 × 4 = $200 cap' },
              { icon: DollarSign, title: 'You earn 20% of their earnings', sub: 'Across all their projects on Mercor' },
              { icon: TrendingUp, title: 'Cap reached at $1,000 earned', sub: '$1,000 × 20% = your $200 cap' },
            ].map((item, i) => (
              <div key={i} className="flex items-start" style={{ gap: '14px' }}>
                <div className="flex items-center justify-center shrink-0" style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#F5F3FF' }}>
                  <item.icon style={{ width: '18px', height: '18px', color: MERCOR_PURPLE }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>{item.title}</p>
                  <p style={{ fontSize: '12px', color: '#6B7280' }}>{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center" style={{ fontSize: '12px', color: MERCOR_PURPLE, marginBottom: '20px', cursor: 'pointer' }}>Read full referral policy →</p>
          <div className="flex items-center" style={{ gap: '12px' }}>
            <button onClick={onBack} className="flex-1 font-medium hover:bg-gray-50"
              style={{ padding: '12px 0', fontSize: '14px', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '10px' }}>Back</button>
            <button onClick={onNext} className="flex-1 font-semibold text-white hover:opacity-90"
              style={{ padding: '12px 0', fontSize: '14px', backgroundColor: MERCOR_PURPLE, borderRadius: '10px' }}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExistingReferralsModal = ({ isOpen, onClose, onBack }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ padding: '16px' }}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white shadow-2xl w-full" style={{ borderRadius: '14px', maxWidth: '520px' }}>
        <div style={{ padding: '32px 36px 28px' }}>
          <div className="flex justify-center" style={{ marginBottom: '20px' }}>
            <div className="flex items-center justify-center" style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#EDE9FE' }}>
              <Share2 style={{ width: '24px', height: '24px', color: MERCOR_PURPLE }} />
            </div>
          </div>
          <h2 className="text-center" style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px' }}>How existing referrals are paid</h2>
          <p className="text-center" style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px', lineHeight: '1.6' }}>
            We compare and pay whichever is <span style={{ fontWeight: 700, color: MERCOR_PURPLE, textDecoration: 'underline' }}>higher</span>.
          </p>
          {/* Examples */}
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px 20px', marginBottom: '12px' }}>
            <p style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', marginBottom: '8px' }}>$50/hr contract · $120 bonus · 7 hrs worked</p>
            <div className="grid grid-cols-2" style={{ gap: '16px' }}>
              <div>
                <p style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '4px' }}>Old Policy</p>
                <p style={{ fontSize: '12px', color: '#374151' }}>$140 × 10 = $12/hr</p>
                <p style={{ fontSize: '14px', fontWeight: 700, color: '#374151' }}>$84</p>
              </div>
              <div>
                <p style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '4px' }}>New Policy</p>
                <p style={{ fontSize: '12px', color: '#374151' }}>$50 × 20% = $10/hr</p>
                <p style={{ fontSize: '14px', fontWeight: 700, color: '#16A34A' }}>$70 ✓</p>
              </div>
            </div>
          </div>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' }}>
            <p style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', marginBottom: '8px' }}>$60/hr contract · $80 bonus · 7 hrs worked</p>
            <div className="grid grid-cols-2" style={{ gap: '16px' }}>
              <div>
                <p style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '4px' }}>Old Policy</p>
                <p style={{ fontSize: '12px', color: '#374151' }}>$80 × 10 = $8/hr</p>
                <p style={{ fontSize: '14px', fontWeight: 700, color: '#374151' }}>$56</p>
              </div>
              <div>
                <p style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '4px' }}>New Policy</p>
                <p style={{ fontSize: '12px', color: '#374151' }}>$60 × 20% = $10/hr</p>
                <p style={{ fontSize: '14px', fontWeight: 700, color: '#16A34A' }}>$70 ✓</p>
              </div>
            </div>
          </div>
          <div className="flex items-center" style={{ gap: '12px' }}>
            <button onClick={onBack} className="flex-1 font-medium hover:bg-gray-50"
              style={{ padding: '12px 0', fontSize: '14px', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '10px' }}>Back</button>
            <button onClick={onClose} className="flex-1 font-semibold text-white hover:opacity-90"
              style={{ padding: '12px 0', fontSize: '14px', backgroundColor: MERCOR_PURPLE, borderRadius: '10px' }}>Take a tour</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══ Main Referrals Page ═══ */
const MercorReferralsPage = () => {
  const [activeTab, setActiveTab] = useState('connections');
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [activeTimePill, setActiveTimePill] = useState('ALL');

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      <Sidebar />
      <div className="flex-1 flex flex-col" style={{ marginLeft: '0px', paddingTop: '56px', paddingBottom: '56px' }}>
        <style>{`@media(min-width:768px){.referrals-main{margin-left:60px!important;padding-top:0!important;padding-bottom:0!important}}`}</style>
        <div className="referrals-main flex-1 flex flex-col" style={{ marginLeft: '0px', paddingTop: '56px', paddingBottom: '56px' }}>
          <TopHeaderBar />
          <div className="flex-1 overflow-y-auto">
            <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px' }}>

              {/* ─── Title ─── */}
              <div className="flex items-center" style={{ gap: '8px', marginBottom: '16px' }}>
                <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#1A1A1A' }}>Referrals</h1>
                <Info style={{ width: '18px', height: '18px', color: '#9CA3AF', cursor: 'pointer' }} />
              </div>

              {/* ─── Tabs + Help ─── */}
              <div className="flex items-center justify-between" style={{ borderBottom: '1px solid #E5E7EB', marginBottom: '24px' }}>
                <div className="flex items-center" style={{ gap: '0px' }}>
                  {[
                    { id: 'connections', label: 'My connections' },
                    { id: 'referrals', label: 'My referrals' },
                  ].map(tab => {
                    const isActive = activeTab === tab.id;
                    return (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        style={{
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: isActive ? MERCOR_PURPLE : '#6B7280',
                          borderBottom: isActive ? `2px solid ${MERCOR_PURPLE}` : '2px solid transparent',
                          marginBottom: '-1px',
                        }}>
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
                <button className="flex items-center hover:bg-gray-50"
                  style={{ gap: '6px', padding: '6px 14px', fontSize: '13px', fontWeight: 500, color: '#374151', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                  <HelpCircle style={{ width: '14px', height: '14px' }} /> Help
                </button>
              </div>

              {/* ═══ MY CONNECTIONS ═══ */}
              {activeTab === 'connections' && (
                <div className="flex flex-col items-center text-center"
                  style={{ border: '1px solid #E5E7EB', borderRadius: '12px', padding: '48px 32px' }}>
                  <div className="flex items-center justify-center"
                    style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#F3F4F6', marginBottom: '20px' }}>
                    <Users style={{ width: '28px', height: '28px', color: '#9CA3AF' }} />
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px' }}>You don't have any connections yet</h3>
                  <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>Turn your LinkedIn connections into referrals earnings</p>
                  <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '32px' }}>Uploading your LinkedIn connections will not trigger any messages. You remain in control.</p>

                  {/* 3-Step Process */}
                  <div className="flex items-center justify-center" style={{ gap: '0px', marginBottom: '32px' }}>
                    {[
                      { num: 1, label: 'Download connections', sub: 'Export your LinkedIn network' },
                      { num: 2, label: 'Upload & match', sub: "We'll find relevant job opportunities" },
                      { num: 3, label: 'Earn rewards', sub: "Get paid when they're hired" },
                    ].map((step, i) => (
                      <React.Fragment key={step.num}>
                        {i > 0 && <div style={{ width: '60px', height: '1px', backgroundColor: '#D1D5DB', marginTop: '-36px' }} />}
                        <div className="flex flex-col items-center" style={{ width: '160px' }}>
                          <div className="flex items-center justify-center"
                            style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: MERCOR_PURPLE, color: '#FFFFFF', fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>
                            {step.num}
                          </div>
                          <p style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', marginBottom: '2px' }}>{step.label}</p>
                          <p style={{ fontSize: '11px', color: '#9CA3AF' }}>{step.sub}</p>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Buttons */}
                  <div className="flex items-center justify-center" style={{ gap: '12px', marginBottom: '24px' }}>
                    <button className="flex items-center font-semibold text-white hover:opacity-90"
                      style={{ gap: '8px', padding: '10px 24px', fontSize: '14px', backgroundColor: MERCOR_PURPLE, borderRadius: '10px' }}>
                      <Upload style={{ width: '16px', height: '16px' }} /> Upload Connections
                    </button>
                    <button className="flex items-center font-semibold hover:bg-gray-50"
                      style={{ gap: '8px', padding: '10px 24px', fontSize: '14px', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '10px' }}>
                      <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
                      Get My Connections
                    </button>
                  </div>

                  <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
                    Extract the ZIP from LinkedIn and upload 'Connections.csv' here.
                  </p>
                  <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
                    Don't have their LinkedIn? Check candidate fit directly.
                  </p>
                  <button className="flex items-center justify-center hover:bg-gray-50"
                    style={{ gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: '#374151', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                    <Users style={{ width: '14px', height: '14px' }} /> Check Candidate Fit
                  </button>
                </div>
              )}

              {/* ═══ MY REFERRALS ═══ */}
              {activeTab === 'referrals' && (
                <div>
                  {/* Track text */}
                  <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>Track your referral earnings and progress.</p>

                  {/* Action buttons row */}
                  <div className="flex items-center justify-between flex-wrap" style={{ gap: '12px', marginBottom: '20px' }}>
                    <div className="flex items-center" style={{ gap: '8px' }}>
                      <button className="flex items-center hover:bg-gray-50"
                        style={{ gap: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 500, color: '#374151', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
                        <Info style={{ width: '13px', height: '13px' }} /> What's new
                      </button>
                      <button className="flex items-center hover:bg-gray-50"
                        style={{ gap: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 500, color: '#374151', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
                        <Share2 style={{ width: '13px', height: '13px' }} /> Share
                      </button>
                    </div>
                    <div className="flex items-center" style={{ gap: '4px' }}>
                      {['1D', '1W', '3D', '7D', 'ALL'].map(pill => (
                        <button key={pill} onClick={() => setActiveTimePill(pill)}
                          style={{
                            padding: '4px 10px', fontSize: '12px', fontWeight: 500, borderRadius: '6px',
                            backgroundColor: activeTimePill === pill ? '#EDE9FE' : 'transparent',
                            color: activeTimePill === pill ? MERCOR_PURPLE : '#6B7280',
                            border: activeTimePill === pill ? `1px solid ${MERCOR_PURPLE}` : '1px solid transparent',
                          }}>
                          {pill}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Stats row */}
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' }}>
                    <div className="grid grid-cols-3 sm:grid-cols-6">
                      {[
                        { label: 'Signed Up', value: '0', active: true },
                        { label: 'Application Started', value: '0' },
                        { label: 'Application Completed', value: '0' },
                        { label: 'Offer Extended', value: '0' },
                        { label: 'Hired', value: '0' },
                        { label: 'Paid', value: '0' },
                      ].map((stat, i) => (
                        <div key={i} className="text-center" style={{
                          padding: '16px 8px',
                          borderRight: i < 5 ? '1px solid #F3F4F6' : 'none',
                          backgroundColor: stat.active ? '#F5F3FF' : '#FFFFFF',
                        }}>
                          <p style={{ fontSize: '12px', fontWeight: 500, color: stat.active ? MERCOR_PURPLE : '#6B7280', marginBottom: '4px' }}>{stat.label}</p>
                          <p style={{ fontSize: '20px', fontWeight: 700, color: stat.active ? MERCOR_PURPLE : '#1A1A1A' }}>{stat.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Status filter */}
                  <div className="flex items-center" style={{ gap: '8px', marginBottom: '16px' }}>
                    <button className="flex items-center"
                      style={{ gap: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 500, color: MERCOR_PURPLE, backgroundColor: '#F5F3FF', border: `1px solid ${MERCOR_PURPLE}`, borderRadius: '6px' }}>
                      <span>Status: Signed Up</span>
                      <ChevronDown style={{ width: '12px', height: '12px' }} />
                    </button>
                  </div>

                  {/* Search */}
                  <div className="relative" style={{ marginBottom: '24px' }}>
                    <Search style={{ width: '16px', height: '16px', color: '#9CA3AF', position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input type="text" placeholder="Search by name or email..."
                      style={{ width: '100%', padding: '10px 12px 10px 36px', fontSize: '13px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', color: '#374151' }}
                      className="focus:border-gray-300" />
                  </div>

                  {/* Empty state */}
                  <div className="flex flex-col items-center text-center" style={{ padding: '48px 0' }}>
                    <div className="flex items-center justify-center"
                      style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#F3F4F6', marginBottom: '16px' }}>
                      <Users style={{ width: '28px', height: '28px', color: '#9CA3AF' }} />
                    </div>
                    <p style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>You don't have any referrals yet</p>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px' }}>All your referrals will be visible here.</p>
                    <button className="flex items-center font-semibold text-white hover:opacity-90"
                      style={{ gap: '8px', padding: '10px 24px', fontSize: '14px', backgroundColor: MERCOR_PURPLE, borderRadius: '10px' }}>
                      <Link2 style={{ width: '16px', height: '16px' }} /> Share Your Referral Link
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Floating chat button */}
      <button className="fixed flex items-center justify-center text-white rounded-full shadow-lg hover:opacity-90 z-50"
        style={{ bottom: '24px', right: '24px', width: '48px', height: '48px', backgroundColor: MERCOR_PURPLE }}>
        <HelpCircle style={{ width: '24px', height: '24px' }} />
      </button>

      {/* Onboarding Modals */}
      <EarningsSharingModal isOpen={onboardingStep === 1} onClose={() => setOnboardingStep(0)} onContinue={() => setOnboardingStep(2)} />
      <EarningsCapModal isOpen={onboardingStep === 2} onClose={() => setOnboardingStep(0)} onNext={() => setOnboardingStep(3)} onBack={() => setOnboardingStep(1)} />
      <ExistingReferralsModal isOpen={onboardingStep === 3} onClose={() => setOnboardingStep(0)} onBack={() => setOnboardingStep(2)} />
    </div>
  );
};

export default MercorReferralsPage;
